import { mapiSmartLocker } from '@/api/SmartLocker'
import { LockerContractStatus } from '@/views/SmartLocker/models'

const defaultLockerContract = () => ({
  status: LockerContractStatus.Active,
  expiresAt: ''
})

const state = {
  // 櫃體群組（例如不同樓層）
  ownerGroupList: [],
  // 智慧櫃合約狀態
  lockerContract: defaultLockerContract(),
  // 是否顯示合約到期提醒橫幅
  isContractAlertBannerShow: false,
  // 是否顯示合約到期提醒對話框
  isContractDialogVisible: false,
  // 到期時是否已有下一份合約（續約）
  hasNextContract: false,
  // 是否有當下有效合約（terminated 或從未簽約時為 false）
  hasCurrentContract: false
}

const getters = {
  // 取得所有群組
  allOwnerGroups: (state) => state.ownerGroupList,
  // 合約到期日完整格式 "YYYY/MM/DD"（對話框、已到期橫幅用）
  displayExpireDate: (state) => {
    const raw = state.lockerContract.expiresAt
    if (!raw) return ''
    return raw.split(' ')[0]
  }
}

const mutations = {
  // 設定群組列表
  setOwnerGroupList (state, data) {
    state.ownerGroupList = data
  },
  // 設定合約狀態
  setLockerContract (state, payload) {
    state.lockerContract = { ...state.lockerContract, ...payload }
  },
  // 重置合約狀態
  resetLockerContract (state) {
    state.lockerContract = defaultLockerContract()
    state.hasCurrentContract = false
  },
  // 設定是否有當下有效合約
  setHasCurrentContract (state, value) {
    state.hasCurrentContract = value
  },
  // 設定合約提醒橫幅顯示狀態
  setContractAlertBannerShow (state, status) {
    state.isContractAlertBannerShow = status
  },
  // 設定合約提醒對話框顯示狀態
  setContractDialogVisible (state, status) {
    state.isContractDialogVisible = status
  },
  // 設定是否有下一份合約
  setHasNextContract (state, value) {
    state.hasNextContract = value
  }
}

const actions = {
  // 取得群組列表
  async fetchGroupList ({ commit }) {
    try {
      const res = await mapiSmartLocker.GetSmartLockerOwnerGroupListForWebAdmin()
      if (res?.data?.Data?.OwnerGroupList) {
        commit('setOwnerGroupList', res.data.Data.OwnerGroupList)
      } else {
        console.warn('無法取得群組列表')
      }
    } catch (err) {
      console.error('取得群組列表失敗：', err)
    }
  },
  // 查詢智慧櫃合約資訊，並推導 status
  async getContractInfo ({ commit }) {
    try {
      const res = await mapiSmartLocker.GetContractInfo()
      const { CurrentContract, NextContract, LastExpiredContract } = res?.data?.Data || {}

      // 無當下合約（已到期、被終止、或從未簽約）
      if (!CurrentContract) {
        commit('setHasCurrentContract', false)
        // 有歷史合約 → 顯示已到期提醒；三者皆 null 表示從未簽約或終止無歷史，不顯示通知
        if (LastExpiredContract) {
          commit('setLockerContract', { status: LockerContractStatus.Expired, expiresAt: LastExpiredContract.EndTime })
          commit('setHasNextContract', !!NextContract)
        }
        return
      }

      commit('setHasCurrentContract', true)

      // 不計費合約不觸發到期提醒
      if (CurrentContract.IsFreeContract) {
        commit('setLockerContract', { status: LockerContractStatus.Active, expiresAt: '' })
        return
      }

      // 用 Math.ceil 確保「30天含」的邊界：不足一天的剩餘時間仍算一天
      const endTime = new Date(CurrentContract.EndTime)
      const now = new Date()
      const daysUntilExpiry = Math.ceil((endTime - now) / (1000 * 60 * 60 * 24))

      if (daysUntilExpiry <= 30 && !NextContract) {
        commit('setLockerContract', { status: LockerContractStatus.Expiring, expiresAt: CurrentContract.EndTime })
        return
      }

      commit('setLockerContract', { status: LockerContractStatus.Active, expiresAt: '' })
    } catch (err) {
      console.error('取得智慧櫃合約資訊失敗：', err)
      commit('resetLockerContract')
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
